import React, {useEffect, useState} from 'react';
import {useCreateRushMutation, useFetchRushOverviewQuery} from '@api';
import {BestScore, RushTypes} from '@types';
import {t} from 'i18next';
import {Link, useNavigate} from 'react-router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowRight, faRankingStar} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import {useRushDailyHighScores} from '@utils/hooks/useRushDailyHighScores';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';

const modes = [
    {type: RushTypes.TYPE_3_MINUTES, key: '3_minutes', label: 'rush_type_3min'},
    {type: RushTypes.TYPE_5_MINUTES, key: '5_minutes', label: 'rush_type_5min'},
    {type: RushTypes.TYPE_UNTIMED, key: 'untimed', label: 'rush_type_untimed'},
];

const leaderboardPath = (type: RushTypes) =>
    `/rushes/leaderboard?tab=global&filter=day&sort=score&rush_type=${type}`;

const RushDailyHighScores = () => {
    const {t} = useTranslation();
    const {data, isLoading: loading, isError, refetch} = useRushDailyHighScores();
    const title = t('index.rush.today_high_scores');
    const linkClassName =
        'inline-flex items-center gap-1 whitespace-nowrap text-inherit no-underline hover:text-neutral-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400 dark:hover:text-neutral-200';

    return (
        <section className="index-home-rush-high-scores" aria-label={title}>
            <div className="index-home-rush-high-scores-content">
                <p
                    className="index-home-rush-high-scores-description m-0 truncate text-xs leading-4 text-neutral-500 dark:text-neutral-400"
                    title={t('index.rush.description')}>
                    {t('index.rush.description')}
                </p>
                <div
                    className="index-home-rush-high-scores-summary flex flex-wrap items-center gap-x-2 text-xs leading-[14px] text-neutral-500 dark:text-neutral-400"
                    aria-busy={!isError && loading}>
                    <Link
                        to={leaderboardPath(RushTypes.TYPE_3_MINUTES)}
                        className={linkClassName}>
                        {title}:
                    </Link>
                    {isError ? (
                        <span className="inline-flex flex-wrap gap-x-2" role="status">
                            <span>{t('common.error')}</span>
                            <button
                                type="button"
                                className="underline"
                                onClick={() => void refetch()}>
                                {t('single_problem.retry')}
                            </button>
                        </span>
                    ) : (
                        modes.map((mode, index) => {
                            const entry = data?.entries.find(
                                score => score.type === `rush.type.${mode.key}`
                            );
                            const label = t(`index.rush.${mode.label}`);
                            return (
                                <Link
                                    key={mode.type}
                                    to={leaderboardPath(mode.type)}
                                    className={linkClassName}
                                    aria-label={`${title} — ${label}${loading ? '' : `: ${entry?.score ?? '—'}`}`}>
                                    <span>{label}</span>
                                    <span className="inline-flex h-[14px] items-center font-medium text-neutral-700 tabular-nums dark:text-neutral-200">
                                        {loading ? (
                                            <ThemeAwareSkeleton
                                                width={16}
                                                height={10}
                                                containerClassName="flex"
                                            />
                                        ) : (
                                            entry?.score ?? '—'
                                        )}
                                    </span>
                                    {index < modes.length - 1 && (
                                        <span
                                            aria-hidden="true"
                                            className="ml-1 text-neutral-400 dark:text-neutral-500">
                                            ·
                                        </span>
                                    )}
                                </Link>
                            );
                        })
                    )}
                </div>
            </div>
        </section>
    );
};

interface RushType {
    type: string;
    bestScore: string | number;
    rushTypeId: number;
}

type RushProps = {
    hideHeader?: boolean;
    stretch?: boolean;
    stretchRows?: boolean;
    showDailyHighScores?: boolean;
};

const Rush: React.FC<RushProps> = ({
    hideHeader = false,
    stretch = false,
    stretchRows = stretch,
    showDailyHighScores = false,
}: RushProps) => {
    const {data: overview} = useFetchRushOverviewQuery(undefined, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
    });
    const bestScores = overview?.bestScores;
    const activeUntimedRush = overview?.activeUntimedRush;
    const [createRush, {data: result, isLoading: loading}] =
        useCreateRushMutation();
    const navigate = useNavigate();

    const [rushTypes, setRushTypes] = useState<RushType[]>([
        {type: t('index.rush.rush_type_3min'), bestScore: '-', rushTypeId: 1},
        {type: t('index.rush.rush_type_5min'), bestScore: '-', rushTypeId: 2},
        {
            type: t('index.rush.rush_type_untimed'),
            bestScore: '-',
            rushTypeId: 3,
        },
    ]);

    useEffect(() => {
        if (result && 'id' in result) {
            navigate(`/rushes/${result.id}`, {
                state: {resumedRush: result.resumed},
            });
        }
    }, [navigate, result]);

    useEffect(() => {
        if (
            Array.isArray(bestScores) &&
            bestScores.every(score => 'type' in score && 'best' in score)
        ) {
            setRushTypes(prevRushTypes =>
                prevRushTypes.map(rushType => {
                    const matchingScore = bestScores.find(
                        (score: BestScore) => score.type === rushType.rushTypeId
                    );
                    return {
                        ...rushType,
                        bestScore: matchingScore ? matchingScore.best : '-',
                    };
                })
            );
        }
    }, [bestScores]);

    return (
        <div
            className={`index-home-feature ${stretch ? 'h-full' : ''}`}>
            {!hideHeader && (
                <div className="index-home-section-heading">
                    <Link
                        to="/rush"
                        className="index-home-heading-link">
                        <span
                            className="index-home-heading-icon index-home-heading-brand-icon index-home-heading-brand-icon--rush"
                            aria-hidden="true"
                        />
                        <span>{t('index.rush.title')}</span>
                    </Link>
                    <div className="index-home-heading-actions">
                        <Link
                            className="index-home-header-icon-link"
                            to="/rushes/leaderboard"
                            title={t('index.leaderboard')}
                            aria-label={`${t('index.rush.title')} ${t('index.leaderboard')}`}>
                            <FontAwesomeIcon icon={faRankingStar} />
                        </Link>
                        <Link
                            to="/rush"
                            aria-label={`${t('index.more')} ${t('index.rush.title')}`}
                            className="index-home-section-link">
                            {t('index.more')}{' '}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </div>
                </div>
            )}
            <div
                className={`card index-home-feature-card rush-card ${stretch ? 'flex-1' : ''}`}>
                <div
                    className={`card-body p-0 ${stretchRows ? 'd-flex flex-column' : ''}`}>
                    <div
                        className={`flex flex-col ${stretchRows ? 'flex-1' : ''}`}>
                        <div className="index-home-table-header grid grid-cols-[minmax(0,1.25fr)_minmax(0,1.75fr)_5.5rem] items-center px-2 py-2 font-semibold">
                            <div className="leading-none">
                                {t('rush.rush_type')}
                            </div>
                            <div className="leading-none">
                                {t('index.best')}
                            </div>
                            <span className="sr-only">
                                {t('common.action')}
                            </span>
                        </div>
                        <div
                            className={`grid ${stretchRows ? 'flex-1 grid-rows-3' : ''}`}>
                            {rushTypes.map(({type, bestScore, rushTypeId}) => (
                                <div
                                    key={rushTypeId}
                                    className={`index-home-table-row grid grid-cols-[minmax(0,1.25fr)_minmax(0,1.75fr)_5.5rem] items-center px-2 ${stretchRows ? '' : 'py-2'}`}>
                                    <div>{type}</div>
                                    <div className="index-home-rush-score">
                                        <span>{bestScore}</span>
                                        {activeUntimedRush &&
                                            rushTypeId === RushTypes.TYPE_UNTIMED && (
                                                <span className="index-home-rush-current-score">
                                                    {`(${t('index.rush.current_score')}: ${activeUntimedRush.score})`}
                                                </span>
                                            )}
                                    </div>
                                    <div className="text-right">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary index-home-table-action"
                                            onClick={() =>
                                                createRush({
                                                    type: rushTypeId,
                                                })
                                            }
                                            disabled={loading}>
                                            {activeUntimedRush &&
                                            rushTypeId === RushTypes.TYPE_UNTIMED
                                                ? t('rush.resume')
                                                : t('index.rush.play')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {showDailyHighScores ? (
                        <RushDailyHighScores />
                    ) : (
                        <div className="index-home-rush-description">
                            <span>{t('index.rush.description')}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Rush;
